import { useEffect, useState } from 'react';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { PageLoader } from '@/components/ui/Spinner';
import { useActiveTenantAccess } from '@/hooks/use-active-tenant';
import { getErrorMessage } from '@/lib/api-error';
import { cn } from '@/lib/utils';
import type { MetaOAuthChannel } from '@/types/integrations';
import { channelSpec } from './integrations-catalog';
import { usePendingPages, useSelectMetaPage } from './use-meta-oauth';

/**
 * Elección de la página de Facebook cuando el usuario administra más de una.
 *
 * Pasa cuando el callback del onboarding OAuth devuelve `?oauth=select&ticket=…`: el backend
 * guardó las páginas candidatas (cifradas, TTL 10 min) y espera que el panel llame a
 * `POST .../meta/select` con la elegida. Con una sola página candidata el backend conecta
 * directo y este diálogo no aparece.
 *
 * Si el ticket expiró, el backend responde 400 ("La sesión de conexión expiró o no existe…"):
 * ese mensaje se muestra tal cual y el usuario puede volver a empezar.
 */

export interface MetaOAuthPagesDialogProps {
  open: boolean;
  /** Ticket de la sesión pendiente (viene en la URL de vuelta). */
  ticket: string | null;
  channel: MetaOAuthChannel | null;
  onClose: () => void;
  /** Se llama al conectar para que la pantalla muestre el aviso de éxito. */
  onConnected: (pageName?: string) => void;
}

export function MetaOAuthPagesDialog({
  open,
  ticket,
  channel,
  onClose,
  onConnected,
}: MetaOAuthPagesDialogProps) {
  const { tenantId } = useActiveTenantAccess();
  const pagesQuery = usePendingPages(tenantId, ticket);
  const selectPage = useSelectMetaPage(tenantId);

  const [selected, setSelected] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const pages = pagesQuery.data?.pages ?? [];

  useEffect(() => {
    if (!open) return;

    // Con una sola página candidata no hay nada que elegir: queda preseleccionada.
    setSelected(pagesQuery.data?.pages.length === 1 ? pagesQuery.data.pages[0].id : null);
    setError(null);
  }, [open, pagesQuery.data]);

  async function handleConfirm() {
    if (!ticket || !selected) return;

    setError(null);
    try {
      const result = await selectPage.mutateAsync({ ticket, pageId: selected });
      onConnected(result.pageName);
      onClose();
    } catch (caught) {
      setError(getErrorMessage(caught));
    }
  }

  const channelLabel = channel ? channelSpec(channel).label : 'el canal';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Elige la página para ${channelLabel}`}
      description="Tu cuenta de Facebook administra varias páginas: elige con cuál va a atender este negocio."
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={selectPage.isPending}>
            Cancelar
          </Button>
          <Button
            onClick={() => void handleConfirm()}
            loading={selectPage.isPending}
            disabled={!selected}
          >
            Conectar la página elegida
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error ? <Alert>{error}</Alert> : null}
        {pagesQuery.isError ? <Alert>{getErrorMessage(pagesQuery.error)}</Alert> : null}

        {pagesQuery.isPending ? (
          <PageLoader label="Buscando tus páginas…" />
        ) : pages.length === 0 ? (
          <p className="text-caption text-ink-muted">
            No hay páginas candidatas en esta sesión. Vuelve a iniciar la conexión con Facebook
            desde la tarjeta del canal.
          </p>
        ) : (
          <ul className="space-y-2">
            {pages.map((page) => (
              <li key={page.id}>
                <label
                  className={cn(
                    'flex cursor-pointer items-center gap-3 rounded-control border px-3.5 py-3 transition-colors',
                    selected === page.id
                      ? 'border-brand-500 bg-brand-50'
                      : 'border-line hover:bg-surface-muted',
                  )}
                >
                  <input
                    type="radio"
                    name="meta-page"
                    className="size-4 accent-brand-600"
                    checked={selected === page.id}
                    onChange={() => setSelected(page.id)}
                  />

                  <span className="min-w-0">
                    <span className="block truncate text-body font-medium text-ink">
                      {page.name}
                    </span>
                    <span className="block truncate text-caption text-ink-muted">
                      {page.igUsername ? `@${page.igUsername} · ` : ''}
                      ID {page.id}
                    </span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}
